/**
 * CommentReplyBanner
 *
 * "Replying to {name}" strip above the comment composer, with its dismiss
 * control. Rendered only while a reply target is selected — Flutter surfaces the
 * same state as the TextField's dynamic hint
 * (`comments_bottom_sheet.dart:1203-1206`: `commentToReply == null ? 'Add
 * comment...' : "Reply to ${commentToReply.userName}"`); a desktop viewport has
 * no tap-overlay dismiss affordance, so the explicit strip is the web equivalent
 * (same convention as the shipped `CommunityReplyBar`).
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { CommentEntity } from '@/domain/entities/comment/commentEntity';

export interface CommentReplyBannerProps {
  /** The comment currently being replied to. */
  replyTo: CommentEntity;
  /** Clears the reply target — wired by the composer to the parent hook. */
  onCancelReply: () => void;
}

export const CommentReplyBanner: React.FC<CommentReplyBannerProps> = ({
  replyTo,
  onCancelReply,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingLeft: responsive.spacing.lg,
        paddingRight: responsive.spacing.lg,
        paddingTop: responsive.spacing.sm,
        paddingBottom: responsive.spacing.sm,
        backgroundColor: theme.backgroundSecondary,
      }}
    >
      <span
        style={{
          fontSize: responsive.font.sm,
          color: theme.text.secondary,
        }}
      >
        {l10n.comments.replyingTo.replace('{name}', replyTo.userName)}
      </span>
      <button
        {...qaAttr({ id: 'comment-reply-cancel' })}
        onClick={onCancelReply}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: theme.text.secondary,
          fontSize: responsive.font.sm,
        }}
      >
        {l10n.common.close}
      </button>
    </div>
  );
};
